import React from "react";

export type ChipTone = "neutral" | "info" | "success" | "warning" | "danger";

interface ChipProps {
    children: React.ReactNode;
    /** Bootstrap Icons name without the "bi-" prefix — "flag", "translate". */
    icon?: string;
    /** Colour from the --status-* tokens. Neutral for plain facts; warning / danger only when it needs attention. */
    tone?: ChipTone;
    /** Hover text — what the chip means ("Country", "Spoken language"). */
    title?: string;
    /**
     * A colour the user picked for the tag (contact tags), as hex. Replaces the
     * tone; the text turns dark or white, whichever reads on it.
     */
    color?: string | null;
}

const DARK_TEXT = "#1e2a2b";

/** WCAG relative luminance of a #rgb / #rrggbb colour, or null when it is not one. */
const luminanceOf = (color: string): number | null => {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    if (!match) return null;
    const hex = match[1].length === 3 ? match[1].split("").map((digit) => digit + digit).join("") : match[1];
    const [red, green, blue] = [0, 2, 4]
        .map((start) => parseInt(hex.slice(start, start + 2), 16) / 255)
        .map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * The colour as the background, with whichever text — dark or white — has
 * more contrast on it (they tie at a luminance of about 0.22). Nothing for a
 * value that is not a hex colour, so the tone applies instead.
 */
const colouredStyle = (color: string | null | undefined): React.CSSProperties | undefined => {
    const luminance = color ? luminanceOf(color) : null;
    if (luminance === null) return undefined;
    return {
        backgroundColor: color!.startsWith("#") ? color! : `#${color}`,
        color: luminance > 0.22 ? DARK_TEXT : "#ffffff",
    };
};

/**
 * A small tonal tag for a single fact on a list card or Quick look — a
 * sub-service, a country, a language, "Profile incomplete". One component so
 * every tag in the lists looks the same.
 */
const Chip: React.FC<ChipProps> = ({ children, icon, tone = "neutral", title, color }) => (
    <span className={`crx-chip crx-chip--${tone}`} title={title} style={colouredStyle(color)}>
        {icon && <i className={`bi bi-${icon}`} aria-hidden="true" />}
        <span className="crx-chip-label">{children}</span>
    </span>
);

export default Chip;
